import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { logout } from '../utils/auth'
import Button from '../components/ui/Button'
import useNotes from '../hooks/useNotes'
import useSelection from '../hooks/useSelection'
import NoteToolbar from '../components/notes/NoteToolbar'
import NoteFilterBar from '../components/notes/NoteFilterBar'
import NoteCard from '../components/notes/NoteCard'
import QuickTodoBoard from '../components/notes/QuickTodoBoard'
import EditModal from '../components/notes/EditModal'
import ImportDialog from '../components/notes/ImportDialog'
import UndoToast from '../components/notes/UndoToast'
import BulkActionBar from '../components/notes/BulkActionBar'
import { filterByKind, filterBySearch, filterByTags, filterByNoteTab, sortNotes } from '../lib/noteFilters'

export default function NotesBoard() {
    const navigate = useNavigate()
    const {
        notes, loading, error, setError, savingIds,
        createNote, patchNote, requestDelete, undoNote, undoAllNotes, pendingDeletes,
        importLines, allTags, bulkPatch,
        createQuickTodo, archiveQuickTodo, deleteQuickTodo,
    } = useNotes()
    const selection = useSelection()

    const [activeKind, setActiveKind] = useState('note')
    const [query, setQuery] = useState('')
    const [noteTab, setNoteTab] = useState('all')
    const [noteSort, setNoteSort] = useState('updated')
    const [activeTags, setActiveTags] = useState(() => new Set())
    const [editingNote, setEditingNote] = useState(null)
    const [showImportDialog, setShowImportDialog] = useState(false)

    const handleKindChange = (kind) => {
        setActiveKind(kind)
        setActiveTags(new Set())
        selection.exitSelectMode()
    }

    const toggleTag = (tag) => {
        setActiveTags((prev) => {
            const next = new Set(prev)
            if (next.has(tag)) next.delete(tag)
            else next.add(tag)
            return next
        })
    }

    const filtered = useMemo(() => {
        let arr = filterByKind(notes, activeKind)
        arr = filterBySearch(arr, query)
        arr = filterByTags(arr, activeTags)
        arr = filterByNoteTab(arr, noteTab)
        return sortNotes(arr, noteSort)
    }, [notes, activeKind, query, noteTab, noteSort, activeTags])

    const closeEditModal = () => setEditingNote(null)

    const handleBulkDelete = () => {
        const toDelete = notes.filter((n) => selection.selected.has(n._id))
        toDelete.forEach((n) => requestDelete(n))
        selection.exitSelectMode()
    }

    const selectedIds = Array.from(selection.selected)

    const handleBulkArchive = async () => {
        await bulkPatch(selectedIds, { archived: true })
        selection.exitSelectMode()
    }

    return (
        <div className="max-w-6xl mx-auto p-6 font-mono">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-serif font-bold tracking-tight text-black dark:text-white">
                    Notes {loading && <span className="text-sm text-zinc-500 font-normal">Loading.</span>}
                </h1>
                <Button
                    variant="destructive"
                    onClick={async () => {
                        await logout()
                        navigate('/login', { state: { from: '/notes' } })
                    }}
                    aria-label="Logout"
                >
                    Logout
                </Button>
            </div>

            {error && (
                <div className="mt-3 border-2 border-red-600 bg-white dark:bg-zinc-950 text-red-600 px-3 py-2 flex items-start justify-between gap-3">
                    <span className="text-sm">{error}</span>
                    <button className="text-sm underline" onClick={() => setError('')} aria-label="Dismiss error">Dismiss</button>
                </div>
            )}

            <NoteToolbar
                activeKind={activeKind}
                onKindChange={handleKindChange}
                query={query}
                onQueryChange={setQuery}
                onNew={() => createNote(activeKind)}
                onImport={() => setShowImportDialog(true)}
                selectMode={selection.selectMode}
                selectedCount={selection.selected.size}
                onToggleSelectMode={() => (selection.selectMode ? selection.exitSelectMode() : selection.enterSelectMode())}
            />

            {activeKind === 'quicktodo' ? (
                <QuickTodoBoard
                    notes={notes}
                    savingIds={savingIds}
                    createQuickTodo={createQuickTodo}
                    patchNote={patchNote}
                    archiveQuickTodo={archiveQuickTodo}
                    deleteQuickTodo={deleteQuickTodo}
                />
            ) : (
                <>
                    <NoteFilterBar
                        tab={noteTab}
                        onTabChange={setNoteTab}
                        sortKey={noteSort}
                        onSortChange={setNoteSort}
                        allTags={allTags}
                        activeTags={activeTags}
                        onToggleTag={toggleTag}
                    />

                    {!loading && filtered.length === 0 && (
                        <div className="mt-10 text-center text-zinc-500">
                            <p>No notes match this filter.</p>
                            <p className="mt-2">Create one to get started.</p>
                        </div>
                    )}

                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {filtered.map((n) => (
                            <NoteCard
                                key={n._id}
                                note={n}
                                saving={savingIds.has(n._id)}
                                onChange={(patch) => patchNote(n._id, patch)}
                                onDelete={() => requestDelete(n)}
                                onEdit={() => setEditingNote(n)}
                                selectMode={selection.selectMode}
                                selected={selection.selected.has(n._id)}
                                onToggleSelect={selection.toggle}
                            />
                        ))}
                    </div>
                </>
            )}

            {editingNote && (
                <EditModal
                    note={editingNote}
                    saving={savingIds.has(editingNote._id)}
                    allTags={allTags}
                    onChange={(patch) => patchNote(editingNote._id, patch)}
                    onClose={closeEditModal}
                    onDelete={() => {
                        requestDelete(editingNote)
                        closeEditModal()
                    }}
                />
            )}

            {showImportDialog && (
                <ImportDialog
                    onCancel={() => setShowImportDialog(false)}
                    onConfirm={async (lines) => {
                        await importLines(lines)
                        setShowImportDialog(false)
                    }}
                />
            )}

            <UndoToast pendingDeletes={pendingDeletes} onUndo={undoNote} onUndoAll={undoAllNotes} />

            {selection.selectMode && (
                <BulkActionBar
                    kind={activeKind}
                    count={selection.selected.size}
                    onSelectAll={() => selection.selectAll(filtered.map((n) => n._id))}
                    onClear={selection.clear}
                    onExit={selection.exitSelectMode}
                    onArchive={handleBulkArchive}
                    onDelete={handleBulkDelete}
                />
            )}
        </div>
    )
}
